/**
 * Session lifecycle configuration.
 *
 * Three limits, and the first one reached ends the session:
 *
 *   - the token's own `exp`, refreshed by renewing while the user is active;
 *   - IDLE_LIMIT_MS, after which nobody renews and the token dies on its own;
 *   - the backend's absolute ceiling, reported as `absolute_limit`.
 */

/** Session states, in the order a healthy session moves through them. */
export const SESSION_STATE = {
  /** Token valid, nothing to do. */
  ACTIVE: 'active',
  /** Close to expiry: warn the user so they can save their work. */
  EXPIRING: 'expiring',
  /** A renewal is in flight. At most one of these exists per tab. */
  RENEWING: 'renewing',
  /** The session ended on its own (TTL, idle, or the absolute ceiling). */
  EXPIRED: 'expired',
  /** The user signed out explicitly. Terminal. */
  CLOSED: 'closed',
};

/** Why a session ended — carried into logs and the message the user sees. */
export const END_REASON = {
  IDLE: 'idle',
  ABSOLUTE_LIMIT: 'absolute_limit',
  UNAUTHENTICATED: 'unauthenticated',
  RENEWAL_FAILED: 'renewal_failed',
  LOGOUT: 'logout',
};

/**
 * Reads one of the session's timings from the environment, in minutes.
 *
 * CRA inlines these at build time: unset — the case everywhere but a
 * developer's machine — and the default stands. Anything that is not a positive
 * number is ignored rather than trusted, because a typo in one of these would
 * silently disable a timeout instead of shortening it.
 */
const minutesFromEnv = (raw, fallbackMinutes) => {
  const minutes = Number(raw);
  return (Number.isFinite(minutes) && minutes > 0 ? minutes : fallbackMinutes) * 60 * 1000;
};

/**
 * No activity for this long and we stop renewing. Deliberately generous: a
 * teacher watching a 40-minute submission never touches the keyboard, and
 * throwing them out mid-evaluation is worse than the marginal risk of a session
 * that outlives its user by a few minutes.
 */
export const IDLE_LIMIT_MS = minutesFromEnv(process.env.REACT_APP_SESSION_IDLE_MINUTES, 60);

/**
 * How long before the idle limit to ask whether the user is still there.
 *
 * Being away from the keyboard should not cost someone their work without a
 * chance to stop it: the dialog appears while the session is still alive, and a
 * single click keeps it. Nothing about it touches the network — presence is a
 * local fact.
 *
 * Three minutes at the production hour — the same warning the absolute ceiling
 * gives, so both dialogs offer the user the same amount of room to finish what
 * they were doing.
 *
 * Capped at a sixth of the limit so a limit shortened for testing stays
 * testable: three minutes of warning on a one-minute limit would mean warning
 * before the session began. A one-minute limit warns ten seconds out.
 */
export const IDLE_WARN_MARGIN_MS = Math.min(3 * 60 * 1000, IDLE_LIMIT_MS / 6);

/**
 * How long before expiry to renew — enough headroom to retry if the network is
 * bad, since three attempts with backoff finish well inside a minute.
 *
 * It also sets what the ceiling dialog can offer. The refusal arrives on the
 * renewal attempt, so whatever is left on the token at that moment is the window
 * the user gets to save their work: three minutes, matching the idle warning.
 */
export const RENEW_MARGIN_MS = minutesFromEnv(process.env.REACT_APP_SESSION_RENEW_MINUTES, 3);

/*
 * There is deliberately no "warn N minutes before expiry" margin.
 *
 * It was tried and removed: a sliding session renews on a cycle, so a margin
 * wider than RENEW_MARGIN_MS re-arms on every single cycle and the user gets a
 * blocking dialog forever, at whatever cadence the TTL implies. And a warning is
 * pointless when the app is about to fix the problem itself.
 *
 * The session interrupts the user in exactly three situations, each of which is
 * something only they can resolve: they have gone quiet (IDLE_WARN_MARGIN_MS),
 * renewal failed and the token is still alive (END_REASON.RENEWAL_FAILED), or
 * the absolute ceiling was reached and nothing will extend it.
 */

/** Retry budget for a renewal that failed for a recoverable reason. */
export const MAX_RENEWAL_ATTEMPTS = 3;
export const RETRY_BASE_DELAY_MS = 250;
export const RETRY_MAX_DELAY_MS = 8000;

/**
 * Activity is recorded at most once per minute. The write itself is cheap, but
 * without a throttle a scroll would fire it hundreds of times a second — and
 * wiring activity straight to renewal is exactly the request storm this whole
 * change exists to remove.
 *
 * Kept below a quarter of the idle limit, which only ever binds when that limit
 * has been shortened for testing: at a one-minute idle limit a flat sixty-second
 * throttle would swallow every click after the first, and the session would
 * expire while the tester was actively using it. Production keeps the full
 * minute, since a quarter of an hour is far larger.
 */
export const ACTIVITY_THROTTLE_MS = Math.min(60 * 1000, IDLE_LIMIT_MS / 4);

/** Timer floor, so a clock jump backwards cannot schedule a busy loop. */
export const MIN_TIMER_DELAY_MS = 1000;

/** localStorage keys. Shared across tabs on purpose. */
export const STORAGE_KEYS = {
  /** Last user activity, so a tab does not expire while its sibling is in use. */
  LAST_ACTIVITY: 'session_last_activity_at',
  /** Bumped on logout/expiry so other tabs follow within the same tick. */
  ENDED_AT: 'session_ended_at',
};

/** DOM events that count as the user being present. */
export const ACTIVITY_EVENTS = ['click', 'keydown', 'scroll', 'touchstart'];
